import { step, paintDisc, blastCrater, wakeRect, fnv1aBytes, fnv1aInt, FNV_OFFSET, Physique } from '@invaders/sim';
import type { Command } from './commands';
import { FP_SHIFT, type GameState } from './state';
import { isInvaderKind } from './invaders';
import { MAX_PLAYERS, applyMatchCommand, matchAfterTick } from './match';
import { UNIT_H, UNIT_W, boxFree, createUnit, jump, placeOnGround, putJetpackOn, stampUnits, startDrill, updateUnit } from './unit';
import { updateProjectiles } from './projectile';
import { fireWeapon, isWeapon } from './weapons';
import { processDeaths, updateSteles } from './legacy';
import { updateChests, spawnChest, CHEST_W, CHEST_H } from './chests';
import { updateMines } from './mines';
import { updateBatteries } from './battery';
import { updateBarrels } from './barrel';
import { updateRoombas } from './roomba';
import { updateVolcanoes } from './volcano';
import { updateSurges } from './surge';
import { aimJet, processBlasts, stopJet, updateFuses, updateJets } from './fire';
import { updateGeysers } from './arsenal';
import { updateMapEvents } from './maps/events';

export const MAX_BRUSH_RADIUS = 64;
export const MAX_EXPLOSION_RADIUS = 64;

const int = (v: number, min: number, max: number) => Math.min(max, Math.max(min, Math.trunc(v) || 0));

function applyCommand(state: GameState, cmd: Command): void {
  switch (cmd.type) {
    case 'paint':
      paintDisc(state.world, int(cmd.x, -1e6, 1e6), int(cmd.y, -1e6, 1e6), int(cmd.radius, 0, MAX_BRUSH_RADIUS), cmd.mat);
      return;
    case 'explode': {
      const r = int(cmd.radius, 1, MAX_EXPLOSION_RADIUS);
      blastCrater(state.world, int(cmd.x, -1e6, 1e6), int(cmd.y, -1e6, 1e6), r);
      state.events.push({ type: 'explosion', x: Math.trunc(cmd.x), y: Math.trunc(cmd.y), radius: r });
      return;
    }
    case 'spawn': {
      const cx = int(cmd.x, -1e6, 1e6) - (UNIT_W >> 1);
      const cy = int(cmd.y, -1e6, 1e6) - (UNIT_H >> 1);
      if (!isInvaderKind(cmd.kind) || !boxFree(state.world, cx, cy) || cx < 0 || cx + UNIT_W > state.world.physique.w) return;
      const unit = createUnit(state.units.length, int(cmd.player, 0, MAX_PLAYERS - 1), cmd.kind, cx, Math.max(0, cy));
      placeOnGround(state.world, unit);
      state.units.push(unit);
      return;
    }
    case 'chest':
      spawnChest(state, int(cmd.x, 0, state.world.physique.w - CHEST_W), int(cmd.content, 0, 255));
      return;
    case 'aim': {
      const angle = Math.trunc(cmd.angle) || 0;
      aimJet(state, cmd.unit, angle);
      const u = state.units[cmd.unit];
      if (u && u.drill > 0) u.drillAngle = angle;
      return;
    }
    case 'release': {
      stopJet(state, cmd.unit);
      const u = state.units[cmd.unit];
      if (u) u.drill = 0;
      return;
    }
    case 'place':
    case 'select':
    case 'lock':
    case 'unlock':
    case 'endTurn':
    case 'ghost':
    case 'forfeit':
      return; // only meaningful under match rules
  }
  const unit = state.units[cmd.unit];
  if (!unit || !unit.alive) return;
  switch (cmd.type) {
    case 'walk': {
      const dir = cmd.dir > 0 ? 1 : cmd.dir < 0 ? -1 : 0;
      unit.walkDir = dir;
      if (dir !== 0) unit.facing = dir;
      else unit.walkAcc = 0;
      return;
    }
    case 'jump':
      jump(state.world, unit);
      return;
    case 'jetpack':
      putJetpackOn(unit);
      return;
    case 'thrust':
      unit.thrust = cmd.on === true && unit.jet > 0;
      return;
    case 'drill':
      startDrill(unit, Math.trunc(cmd.angle) || 0);
      return;
    case 'fire':
      if (isWeapon(cmd.weapon)) {
        const target: [number, number] | undefined = cmd.tx !== undefined ? [int(cmd.tx, 0, state.world.physique.w - 1), int(cmd.ty ?? 0, 0, state.world.physique.h - 1)] : undefined;
        const remote = cmd.sx !== undefined && target ? { sx: int(cmd.sx, -1e6, 1e6), sy: int(cmd.sy ?? 0, -1e6, 1e6), tx: target[0], ty: target[1], rot: int(cmd.rot ?? 0, -1e6, 1e6) } : undefined;
        fireWeapon(state, unit, cmd.weapon, Math.trunc(cmd.angle) || 0, int(cmd.power, 0, 100), target, { link: cmd.link !== undefined ? int(cmd.link, -1, 1e6) : undefined, remote });
      }
      return;
  }
}

/**
 * Advances the game by one tick. Only commands stamped with the current tick are applied, in array order,
 * and under match rules only those the rules accept. Mutates and returns `state`.
 */
export function tick(state: GameState, commands: readonly Command[]): GameState {
  state.events.length = 0;
  for (const cmd of commands) {
    if (cmd.tick !== state.tick) continue;
    if (state.match && !applyMatchCommand(state, cmd)) continue;
    applyCommand(state, cmd);
  }
  for (const unit of state.units) if (unit.alive) updateUnit(state, unit);
  updateProjectiles(state);
  updateSurges(state);
  updateJets(state);
  updateGeysers(state);
  updateFuses(state);
  updateMines(state);
  updateBatteries(state);
  updateBarrels(state);
  updateRoombas(state);
  updateVolcanoes(state);
  updateSteles(state);
  updateChests(state);

  stampUnits(state.world, state.units, true);
  stampChests(state, true);
  state.world.wind = state.wind;
  step(state.world);
  stampChests(state, false);
  stampUnits(state.world, state.units, false);
  updateMapEvents(state);
  processBlasts(state);

  if (state.match) matchAfterTick(state);
  processDeaths(state);
  state.tick++;
  return state;
}

/** Chests are stamped like units so that sand buries them. */
function stampChests(state: GameState, stamp: boolean): void {
  const { w, h, data } = state.world.physique;
  const from = stamp ? Physique.EMPTY : Physique.ENTITY;
  const to = stamp ? Physique.ENTITY : Physique.EMPTY;
  for (const c of state.chests) {
    const cx = c.x >> FP_SHIFT;
    const cy = c.y >> FP_SHIFT;
    for (let y = Math.max(0, cy); y < Math.min(h, cy + CHEST_H); y++) {
      for (let x = Math.max(0, cx); x < Math.min(w, cx + CHEST_W); x++) if (data[y * w + x] === from) data[y * w + x] = to;
    }
    wakeRect(state.world, cx, cy, cx + CHEST_W - 1, cy + CHEST_H - 1);
  }
}

export function hashState(state: GameState): number {
  let h = FNV_OFFSET;
  h = fnv1aInt(h, state.tick);
  h = fnv1aInt(h, state.world.rng.state);
  h = fnv1aInt(h, state.wind);
  h = fnv1aBytes(h, state.world.physique.data);
  h = fnv1aBytes(h, state.world.cellState.data);
  for (const u of state.units) {
    h = fnv1aInt(h, u.player | (u.kind << 8));
    h = fnv1aInt(h, u.x);
    h = fnv1aInt(h, u.y);
    h = fnv1aInt(h, u.vx);
    h = fnv1aInt(h, u.vy);
    h = fnv1aInt(h, u.hp);
    h = fnv1aInt(h, (u.alive ? 1 : 0) | ((u.facing + 1) << 1) | ((u.walkDir + 1) << 3) | ((u.puppet ? 1 : 0) << 5));
    h = fnv1aInt(h, u.walkAcc);
    h = fnv1aInt(h, u.fallStartY);
    h = fnv1aInt(h, u.fuse | ((u.burning ? 1 : 0) << 16) | ((u.wet ? 1 : 0) << 17) | (u.frozen << 18) | (u.slide << 21));
    h = fnv1aInt(h, u.jet | ((u.thrust ? 1 : 0) << 16) | ((u.airJump ? 1 : 0) << 17));
    // Only while it drills: the hashes of matches without a drill stay what they were.
    if (u.drill > 0) { h = fnv1aInt(h, u.drill); h = fnv1aInt(h, u.drillAngle); }
  }
  for (const p of state.projectiles) {
    h = fnv1aInt(h, p.x);
    h = fnv1aInt(h, p.y);
    h = fnv1aInt(h, p.vx);
    h = fnv1aInt(h, p.vy);
    h = fnv1aInt(h, p.owner | (p.weapon << 16));
    h = fnv1aInt(h, p.age);
    h = fnv1aInt(h, p.link);
    if (p.hit) for (const id of p.hit) h = fnv1aInt(h, id);
    if (p.marks) for (const v of p.marks) h = fnv1aInt(h, v);
    if (p.walls) h = fnv1aInt(h, p.walls);
  }
  for (const c of state.chests) {
    h = fnv1aInt(h, c.id);
    h = fnv1aInt(h, c.x);
    h = fnv1aInt(h, c.y);
    h = fnv1aInt(h, c.vx);
    h = fnv1aInt(h, c.vy);
    h = fnv1aInt(h, c.mode | (c.content << 8) | ((c.mimic ? 1 : 0) << 16));
    h = fnv1aInt(h, c.heat);
    h = fnv1aInt(h, c.until);
    if (c.hops !== 0) h = fnv1aInt(h, c.hops);
  }
  h = fnv1aInt(h, state.nextChest);
  for (const g of state.geysers) {
    h = fnv1aInt(h, g.x);
    h = fnv1aInt(h, g.y);
    h = fnv1aInt(h, g.ticks);
    h = fnv1aInt(h, g.wait);
    h = fnv1aInt(h, g.turn);
  }
  for (const j of state.jets) {
    h = fnv1aInt(h, j.unit);
    h = fnv1aInt(h, j.angle);
    h = fnv1aInt(h, j.ticks);
  }
  for (const su of state.surges) {
    h = fnv1aInt(h, su.x);
    h = fnv1aInt(h, su.dir);
    h = fnv1aInt(h, su.floor);
    h = fnv1aInt(h, su.height);
    h = fnv1aInt(h, su.travelled);
    h = fnv1aInt(h, su.carried);
    h = fnv1aInt(h, su.owner | ((su.electric ? 1 : 0) << 16) | ((su.burning ? 1 : 0) << 17) | ((su.eaten ? 1 : 0) << 18));
    for (const id of su.hit) h = fnv1aInt(h, id);
  }
  for (const mi of state.mines) {
    h = fnv1aInt(h, mi.x);
    h = fnv1aInt(h, mi.y);
    h = fnv1aInt(h, mi.arm);
    h = fnv1aInt(h, mi.owner | (mi.kind << 16));
    // Only when moving (a jump pad, a belt): the hashes of games without them stay what they were.
    if (mi.vy || mi.fx || mi.fy) { h = fnv1aInt(h, mi.vy ?? 0); h = fnv1aInt(h, mi.fx ?? 0); h = fnv1aInt(h, mi.fy ?? 0); }
  }
  for (const b of state.batteries) {
    h = fnv1aInt(h, b.x);
    h = fnv1aInt(h, b.y);
    h = fnv1aInt(h, b.owner | (b.turns << 16));
    h = fnv1aInt(h, b.tick);
  }
  for (const b of state.barrels) {
    h = fnv1aInt(h, b.x);
    h = fnv1aInt(h, b.y);
    h = fnv1aInt(h, b.vx);
    h = fnv1aInt(h, b.vy);
    h = fnv1aInt(h, b.weapon | (b.turns << 8));
    h = fnv1aInt(h, b.owner);
    h = fnv1aInt(h, b.tick);
    h = fnv1aInt(h, b.load);
  }
  for (const r of state.roombas) {
    h = fnv1aInt(h, r.x);
    h = fnv1aInt(h, r.y);
    h = fnv1aInt(h, r.vy);
    h = fnv1aInt(h, (r.dir + 1) | (r.turns << 2) | ((r.sucking ? 1 : 0) << 8) | (r.ate << 9) | (r.sight << 17) | ((r.behind ? 1 : 0) << 22));
    h = fnv1aInt(h, r.owner);
    h = fnv1aInt(h, r.tick);
  }
  for (const v of state.volcanoes) {
    h = fnv1aInt(h, v.x);
    h = fnv1aInt(h, v.y);
    h = fnv1aInt(h, v.owner);
    h = fnv1aInt(h, v.turns | (v.tick << 8));
  }
  for (const st of state.steles) {
    h = fnv1aInt(h, st.x);
    h = fnv1aInt(h, st.y);
    h = fnv1aInt(h, st.kind | (st.player << 8) | (st.stage << 16));
  }
  // The map's features (not on a plain island, so that the step-1 reference keeps its hash).
  const mp = state.map;
  if (mp.gate || mp.crater || mp.airless) {
    h = fnv1aInt(h, mp.gravityDiv | ((mp.airless ? 1 : 0) << 4) | ((mp.cauldron ? 1 : 0) << 5) | ((mp.magazine ? 1 : 0) << 6) | (mp.eruptionIn << 8));
  }
  const m = state.match;
  if (m) {
    h = fnv1aInt(h, m.phase | (m.currentPlayer << 8) | ((m.locked ? 1 : 0) << 16) | ((m.moved ? 1 : 0) << 17) | ((m.fired ? 1 : 0) << 18));
    h = fnv1aInt(h, m.phaseTick);
    h = fnv1aInt(h, m.turn);
    h = fnv1aInt(h, m.round);
    h = fnv1aInt(h, m.selected);
    h = fnv1aInt(h, m.winner);
    for (const r of m.rotation) h = fnv1aInt(h, r);
    for (const a of m.ammo) h = fnv1aInt(h, a);
    h = fnv1aInt(h, m.lavaLevel);
    h = fnv1aInt(h, m.pistolShots);
    // Only when set: the hashes of matches without an exclusion nor a burn in lava stay what they were.
    if (m.out !== 0 || m.lavaBurnt) h = fnv1aInt(h, m.out | ((m.lavaBurnt ? 1 : 0) << 16));
  }
  return h;
}

export function hashToHex(hash: number): string {
  return (hash >>> 0).toString(16).padStart(8, '0');
}
